// Trendi — AI persona context. Brand-aligned (teal/orange palette of Trendy Smart).
// Note: Persian display name is "ترندی"; English/Latin spelling is "Trendi".
"use client";

import React, { createContext, useContext, useState } from "react";

export type TrendyMood =
  "idle" | "thinking" | "happy" | "alert" | "typing" | "celebrating" | "sleeping";

export type TrendyTone = "friendly" | "formal" | "playful";

// Accents map to brand palette tokens (defined in src/styles.css).
export type TrendyAccent = "teal" | "orange" | "blue" | "amber";

export interface TrendyPersona {
  name: string; // localized display name (default: ترندی)
  latin: string; // latin spelling for Latin contexts (default: Trendi)
  tone: TrendyTone;
  mood: TrendyMood;
  accent: TrendyAccent;
}

interface Ctx {
  persona: TrendyPersona;
  setName: (n: string) => void;
  setLatin: (n: string) => void;
  setTone: (t: TrendyTone) => void;
  setMood: (m: TrendyMood) => void;
  setAccent: (a: TrendyAccent) => void;
}

const TrendyCtx = createContext<Ctx | null>(null);

export function TrendyProvider({
  children,
  initial,
}: {
  children: React.ReactNode;
  initial?: Partial<TrendyPersona>;
}) {
  const [persona, setPersona] = useState<TrendyPersona>({
    name: initial?.name ?? "ترندی",
    latin: initial?.latin ?? "Trendi",
    tone: initial?.tone ?? "friendly",
    mood: initial?.mood ?? "idle",
    accent: initial?.accent ?? "teal",
  });
  const value = React.useMemo<Ctx>(
    () => ({
      persona,
      setName: (n) => setPersona((p) => (p.name === n ? p : { ...p, name: n })),
      setLatin: (n) => setPersona((p) => (p.latin === n ? p : { ...p, latin: n })),
      setTone: (t) => setPersona((p) => (p.tone === t ? p : { ...p, tone: t })),
      setMood: (m) => setPersona((p) => (p.mood === m ? p : { ...p, mood: m })),
      setAccent: (a) => setPersona((p) => (p.accent === a ? p : { ...p, accent: a })),
    }),
    [persona],
  );

  return <TrendyCtx.Provider value={value}>{children}</TrendyCtx.Provider>;
}

const NOOP_CTX: Ctx = {
  persona: { name: "ترندی", latin: "Trendi", tone: "friendly", mood: "idle", accent: "teal" },
  setName: () => {},
  setLatin: () => {},
  setTone: () => {},
  setMood: () => {},
  setAccent: () => {},
};

export function useAiPersona(): Ctx {
  return useContext(TrendyCtx) ?? NOOP_CTX;
}

// Accent → brand color helpers (use these instead of arbitrary hex)
export const ACCENT_HEX: Record<TrendyAccent, string> = {
  teal: "#009688",
  orange: "#FE964A",
  blue: "#0277BD",
  amber: "#F59E0B",
};

export const ACCENT_HEX_SOFT: Record<TrendyAccent, string> = {
  teal: "#33AFA0",
  orange: "#FFB077",
  blue: "#38BDF8",
  amber: "#FCD34D",
};

// Microcopy by tone + situation
export function trendySay(
  tone: TrendyTone,
  key: "greet" | "thinking" | "suggest" | "alertSpike" | "queueWorking" | "celebrate" | "learn",
): string {
  const dict: Record<TrendyTone, Record<string, string>> = {
    friendly: {
      greet: "سلام رفیق ✨ من اینجام کنارت، بریم پیجتو حسابی بترکونیم؟",
      thinking: "یه لحظه… دارم به این فکر می‌کنم 🤔",
      suggest: "این پاسخ نرخ تعامل بیشتری داره — پیشنهاد می‌دم استفاده کنی.",
      alertSpike: "متوجه شدم پیامات یهو خیلی زیاد شده! یه نگاه بنداز.",
      queueWorking: "دارم صف ارسال رو پایش می‌کنم، خیالت راحت.",
      celebrate: "ایول! یه برنده داریم 🎉",
      learn: "از این گفتگو یاد گرفتم — دفعه‌ی بعد بهتر جواب می‌دم.",
    },
    formal: {
      greet: "سلام. دستیار هوشمند شما در خدمت است.",
      thinking: "در حال پردازش…",
      suggest: "براساس داده‌های تعامل، این پاسخ بازده بالاتری دارد.",
      alertSpike: "افزایش غیرعادی در حجم پیام‌ها مشاهده شد.",
      queueWorking: "صف ارسال در حال پایش است.",
      celebrate: "قرعه‌کشی با موفقیت انجام شد.",
      learn: "الگوی جدید آموخته شد.",
    },
    playful: {
      greet: "اومدم! آماده‌ایم کلی کار باحال کنیم؟ 🚀",
      thinking: "هووم… داره چرخای مغزم می‌چرخن ⚙️",
      suggest: "این یکی توپه! امتحانش کن 😉",
      alertSpike: "اوهو! یهو شلوغ شد اینجا 👀",
      queueWorking: "صف ارسال؟ سپردمش به خودم 🛡️",
      celebrate: "بوووم! 🎊 برنده داریم!",
      learn: "پلاس‌وان به هوشم اضافه شد ✨",
    },
  };
  return dict[tone][key];
}
